import { FC, useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { ChevronDown, Check, X } from "lucide-react";
import usePricing from "../../../hooks/usePricing";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { DiscountSliceAction } from "../../../redux/slice/discountSlice";
import { DiscountMode, IDiscountResponse } from "../../../typings";
import ManagerCodeModal from "../../common/manager-code-modal/ManagerCodeModal";

export interface IDiscountModalProps {
  open: boolean;
  onClose: () => void;
}

const NONE_DISCOUNT_ID = "none";

function RadioRow({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className="flex items-center gap-[0.5rem] text-left text-[0.9375rem] font-medium text-[#515151] disabled:opacity-50"
    >
      <span className="flex h-[1.5rem] w-[1.5rem] items-center justify-center rounded-full border-[0.1875rem] border-[#515151] transition-all">
        {checked ? <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#515151]" /> : null}
      </span>
      <span>{label}</span>
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[1.0625rem] font-semibold text-[#515151]">
      {children}
    </label>
  );
}

function SelectField<T extends string>({
  value,
  onChange,
  options,
  disabled,
  testName,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  disabled?: boolean;
  testName?: string;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        test-name={testName}
        className="h-[3rem] w-full appearance-none rounded-[0.75rem] border border-transparent bg-[#F4F4F4] px-[1rem] pr-[2.75rem] text-[0.9375rem] font-medium text-[#515151] outline-none transition focus:border-[#0079C2] disabled:opacity-50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-[0.75rem] top-1/2 -translate-y-1/2 text-[#515151]"
        size={18}
      />
    </div>
  );
}

const DiscountModal: FC<IDiscountModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { categoryList } = usePricing();
  const {
    getDiscountByStoreResponse,
    selectedDiscount,
    eligibleDiscounts,
    customDiscount: customDiscountRedux,
  } = useAppSelector((state) => state.discount);
  const { cartMode, billById } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);

  const customDiscounts = [
    { value: "percentage", label: "Percentage" },
    { value: "buy1getxfree", label: "Buy 1 get x free" },
    { value: "fixed", label: "Fixed cash reduction" },
    { value: "free", label: "Free Service" },
  ] as const;

  const coverages = [
    { value: "whole", label: "Whole billing session" },
    { value: "bill", label: "This bill only" },
  ] as const;

  const services = [
    { id: 999999, categoryName: "All" },
    ...(categoryList?.body.filter((tab) => tab.showInMenu) || []),
    { id: -100000, categoryName: "Alteration" },
  ];

  const [discountType, setDiscountType] = useState<"defined" | "custom">("defined");
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [definedDiscount, setDefinedDiscount] = useState<string | undefined>();
  const [customDiscount, setCustomDiscount] = useState<string>(customDiscounts[0].value);
  const [coverage, setCoverage] = useState<string>(coverages[0].value);
  const [amount, setAmount] = useState<number>(0);
  const [service, setService] = useState<string>("999999");

  const isDisabled = cartMode === "pickup";

  const handleDiscountTypeChange = (value: "defined" | "custom") => {
    if (value === "custom") {
      setIsAccessModalOpen(true);
    } else {
      setDiscountType(value);
    }
  };

  useEffect(() => {
    if (open) {
      if (selectedDiscount && selectedDiscount !== NONE_DISCOUNT_ID) {
        setDefinedDiscount(selectedDiscount);
      } else if (eligibleDiscounts && eligibleDiscounts.id !== "custom-discount") {
        setDefinedDiscount(eligibleDiscounts.id);
      } else {
        setDefinedDiscount(NONE_DISCOUNT_ID);
      }
    }
  }, [open, selectedDiscount, eligibleDiscounts]);

  // Reset the whole form whenever the discount state is cleared (order
  // created / customer session ended), so stale selections don't leak
  // into the next customer or bill.
  useEffect(() => {
    if (!selectedDiscount && !eligibleDiscounts && !customDiscountRedux) {
      setDiscountType("defined");
      setDefinedDiscount(NONE_DISCOUNT_ID);
      setCustomDiscount(customDiscounts[0].value);
      setCoverage(coverages[0].value);
      setAmount(0);
      setService("999999");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDiscount, eligibleDiscounts, customDiscountRedux]);

  useEffect(() => {
    if (customDiscount === "free") {
      setAmount(100);
    } else {
      setAmount(0);
    }
  }, [customDiscount]);

  useEffect(() => {
    if (customDiscount === "percentage" && amount >= 100) {
      setAmount(100);
    } else if (amount <= 0) {
      setAmount(0);
    }
  }, [amount]);

  const onClickApply = () => {
    if (discountType === "custom") {
      let mode = DiscountMode.FIXED;
      if (customDiscount === "percentage" || customDiscount === "free") {
        mode = DiscountMode.PERCENTAGE;
      }

      let serviceIds: number[] = [];
      const serviceIdNum = Number(service);
      if (serviceIdNum === 999999) {
        serviceIds = categoryList?.body.map((cat) => cat.id) || [];
      } else {
        serviceIds = [serviceIdNum];
      }

      const customDiscountObj: IDiscountResponse = {
        id: "custom-discount",
        discountName: "Custom Discount",
        discountAmount: amount,
        discountMode: mode,
        deleted: false,
        description: "Manually applied custom discount",
        createdTime: new Date().toISOString(),
        lastModifiedTime: new Date().toISOString(),
        merchantId: 0,
        storeId: 0,
        serviceIds,
        minimumBillValue: 0,
        enabled: true,
        autoApply: false,
        applyToAnyUser: true,
      };

      dispatch(DiscountSliceAction.setCustomDiscount(customDiscountObj));
      dispatch(DiscountSliceAction.setSelectedDiscount(null));
    } else {
      dispatch(DiscountSliceAction.setSelectedDiscount(definedDiscount));
      dispatch(DiscountSliceAction.setCustomDiscount(null));
    }
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        disableScrollLock
        disableRestoreFocus
        sx={{ zIndex: 999 }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0,0,0,0.5)" },
          },
        }}
      >
        <div className="flex h-full w-full items-center justify-center p-[2rem]">
          <div className="w-full max-w-[61.25rem] rounded-[1.75rem] border border-[#D9D9D9] bg-white shadow-[0_1.25rem_3.75rem_rgba(0,0,0,0.08)]">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#EAEAEA] px-[2.5rem] py-[1.75rem]">
              <div>
                <h1 className="text-[2.625rem] font-bold leading-none tracking-[-0.02em] text-[#515151]">
                  Discounts
                </h1>
                <p className="mt-[0.5rem] text-[0.9375rem] text-[#515151]/70">
                  Apply either a predefined promotion or create a custom discount for the ticket.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-[2.75rem] w-[2.75rem] items-center justify-center rounded-full border-[0.125rem] border-[#515151]/70 text-[#515151] transition hover:bg-[#F7F7F7]"
                aria-label="Close"
              >
                <X size={22} strokeWidth={2.2} />
              </button>
            </div>

            {/* Body */}
            <div className="px-[2.5rem] py-[2rem]">
              {/* Mode row */}
              <div className="grid gap-[2rem] lg:grid-cols-[11.25rem_minmax(0,1fr)]">
                <div className="pt-[0.25rem] text-[1.0625rem] font-semibold text-[#515151]">
                  Discount Type
                </div>
                <div className="flex flex-col gap-[1rem] sm:flex-row sm:items-center sm:gap-[2.5rem]">
                  <RadioRow
                    label="Defined Discount"
                    checked={discountType === "defined"}
                    onChange={() => handleDiscountTypeChange("defined")}
                    disabled={isDisabled}
                  />
                  <RadioRow
                    label="Custom Discount"
                    checked={discountType === "custom"}
                    onChange={() => handleDiscountTypeChange("custom")}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              {discountType === "custom" ? (
                <div className="mt-[2rem] space-y-[1.5rem]">
                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Discount Type</FieldLabel>
                    <SelectField
                      value={customDiscount}
                      onChange={setCustomDiscount}
                      options={customDiscounts}
                      disabled={isDisabled}
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Coverage</FieldLabel>
                    <SelectField
                      value={coverage}
                      onChange={setCoverage}
                      options={coverages}
                      disabled={isDisabled}
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Amount</FieldLabel>
                    <div className="relative">
                      <input
                        type="number"
                        autoComplete="off"
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        disabled={customDiscount === "free" || isDisabled}
                        placeholder={customDiscount === "free" ? "N/A" : "Enter amount"}
                        className="h-[3rem] w-full rounded-[0.75rem] border border-transparent bg-[#F4F4F4] px-[1rem] text-[0.9375rem] font-medium text-[#515151] outline-none transition focus:border-[#0079C2] disabled:opacity-50"
                      />
                      {(customDiscount === "percentage" || customDiscount === "free") && (
                        <span className="pointer-events-none absolute right-[1rem] top-1/2 -translate-y-1/2 text-[0.875rem] font-semibold text-[#515151]/60">
                          %
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Service</FieldLabel>
                    <SelectField
                      value={service}
                      onChange={setService}
                      options={services.map((s) => ({
                        value: String((s as any).id),
                        label: s.categoryName,
                      }))}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-[2rem] space-y-[1.5rem]">
                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-center">
                    <FieldLabel>Discount</FieldLabel>
                    <SelectField
                      value={definedDiscount ?? NONE_DISCOUNT_ID}
                      onChange={setDefinedDiscount}
                      options={[
                        { value: NONE_DISCOUNT_ID, label: "None" },
                        ...(getDiscountByStoreResponse?.body
                          .filter((d) => d.enabled)
                          .map((d) => ({ value: d.id, label: d.discountName })) ?? []),
                      ]}
                      disabled={isDisabled}
                      testName="defined-discount-select"
                    />
                  </div>

                  <div className="grid gap-[1rem] lg:grid-cols-[11.25rem_minmax(0,1fr)] lg:items-start">
                    <FieldLabel>Description</FieldLabel>
                    <div
                      test-name="defined-discount-description"
                      className="min-h-[8.375rem] rounded-[1rem] bg-[#F4F4F4] px-[1.25rem] py-[1rem] text-[0.9375rem] leading-[1.75rem] text-[#515151]/45"
                    >
                      {definedDiscount === NONE_DISCOUNT_ID
                        ? "No discount applied to this ticket."
                        : getDiscountByStoreResponse?.body.find(
                            (d) => d.id === definedDiscount
                          )?.description ?? ""}
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-[2.5rem] flex items-center justify-end gap-[1rem] border-t border-[#EAEAEA] pt-[2rem]">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-[3.5rem] rounded-[1rem] border border-[#D9D9D9] bg-white px-[1.75rem] text-[1.125rem] font-semibold text-[#515151] transition hover:bg-[#F8F8F8]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onClickApply}
                  disabled={isDisabled}
                  test-name="apply-discount-button"
                  className="flex h-[3.5rem] min-w-[13.75rem] items-center justify-center gap-[0.5rem] rounded-[1rem] bg-[#018752] px-[2rem] text-[1.125rem] font-semibold text-white shadow-[0_0.625rem_1.5rem_rgba(1,135,82,0.22)] transition hover:brightness-95 active:scale-[0.99] disabled:opacity-50"
                >
                  <Check size={20} strokeWidth={2.4} />
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ManagerCodeModal
        open={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        title="Manager Approval"
        task="apply a custom discount"
        onDone={() => {
          setDiscountType("custom");
          setIsAccessModalOpen(false);
        }}
        actionType="DISCOUNT_DRAWER_ACCESS"
        requestedByUserId={user?.id}
        billId={billById?.id}
      />
    </>
  );
};

export default DiscountModal;
